import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { TreoNavigationModule } from 'tsui/@treo';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatSortModule } from '@angular/material/sort';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { TreoDateRangeModule } from 'tsui/@treo';
import { SharedModule } from 'app/shared/shared.module';
import { TreoFindByKeyPipeModule } from 'tsui/@treo';
import { MatDividerModule } from '@angular/material/divider';
import {
  MAT_LEGACY_AUTOCOMPLETE_SCROLL_STRATEGY as MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
  MatLegacyAutocompleteModule as MatAutocompleteModule,
} from '@angular/material/legacy-autocomplete';
import { MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { NgxMapboxGLModule } from 'ngx-mapbox-gl';
import moment from 'moment';

import { PropertiesDetailsComponent } from './details/details.component';
import { PropertiesListComponent } from './list/list.component';
import { PropertiesListTableComponent } from './list/table/table.component';
import { PropertiesListMapComponent } from './list/map/map.component';
import { PropertiesComponent } from './properties.component';
import { PropertiesHeaderComponent } from './details/header/header.component';
import { PropertyDealAddComponent } from './modals/deal-add/deal-add.component';
import { PropertyAddAddressComponent } from './modals/property-add-address/property-add-address.component';
import { propertiesRoutes } from 'app/modules/properties/properties.routing';
import { PropertyAddContactComponent } from './modals/property-add-contact/property-add-contact.component';
import { PropertyChangeTemplateComponent } from './modals/property-change-template/property-change-template.component';
import { Overlay } from '@angular/cdk/overlay';
import { DigitOnlyModule } from '@uiowa/digit-only';
import { environment } from '@env/environment';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import { ChooseParentPortfolioComponent } from './modals/choose-parent-portfolio/choose-parent-portfolio.component';
import { SavedViewSidebarComponent } from 'app/shared/components/saved-view-sidebar/saved-view-sidebar.component';
import {
  TsPageHeaderModule,
  TsPaginatorModule,
  TsBreadcrumbModule,
  TsButtonModule,
  TSIconModule,
  TsBadgeModule,
  TsOverlayModule,
  TsFilterModule,
  TsInputComponent,
  TsPageComponent,
  TsMenuComponent,
} from 'tsui';
import { TsPipesModule } from 'tsui/lib/cross-cutting/pipes/ts-pipes.module';
import { TsTableComponent } from 'tsui/lib/ts-table';
import { NgxsModule } from '@ngxs/store';
import { AssetManagementState } from '../asset-management/state/asset-management.state';
import { HasFeaturePipe } from '@shared/pipes/has-feature.pipe';
import { HasPermissionPipe } from '@shared/pipes/has-permission.pipe';

const TSUI_MODULES = [
  TsPipesModule,
  TsPageHeaderModule,
  TsPageComponent,
  TsPaginatorModule,
  TsBreadcrumbModule,
  TsButtonModule,
  TSIconModule,
  TsBadgeModule,
  TsOverlayModule,
  TsFilterModule,
  TsInputComponent,
];

@NgModule({
  declarations: [
    PropertiesComponent,
    PropertiesListComponent,
    PropertiesListTableComponent,
    PropertiesDetailsComponent,
    PropertiesListMapComponent,
    PropertyDealAddComponent,
    PropertiesHeaderComponent,
    PropertyAddAddressComponent,
    PropertyChangeTemplateComponent,
    PropertyAddContactComponent,
    ChooseParentPortfolioComponent,
  ],
  imports: [
    RouterModule.forChild(propertiesRoutes),
    NgxsModule.forFeature([AssetManagementState]),
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatMenuModule,
    DragDropModule,
    MatMomentDateModule,
    MatRadioModule,
    MatSelectModule,
    MatPaginatorModule,
    MatAutocompleteModule,
    TreoNavigationModule,
    MatTableModule,
    MatSidenavModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatDividerModule,
    TreoFindByKeyPipeModule,
    TreoDateRangeModule,
    SharedModule,
    DigitOnlyModule,
    MatChipsModule,
    NgxMapboxGLModule.withConfig({ accessToken: environment.mapbox.accessToken }),
    SavedViewSidebarComponent,
    TsTableComponent,
    ...TSUI_MODULES,
    HasFeaturePipe,
    TsMenuComponent,
    HasPermissionPipe,
  ],
  providers: [
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: moment.ISO_8601,
        },
        display: {
          dateInput: 'LL',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
    {
      provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
      useFactory: (overlay: Overlay) => {
        return () => overlay.scrollStrategies.block();
      },
      deps: [Overlay],
    },
  ],
})
export class PropertiesModule {}
